import type { ReactNode } from "react"
import EditableText from "../ui/EditableText"

interface LandingTextSectionProps {
  heading: string
  subheading?: string
  children?: ReactNode
  /** Blue section on white text instead of the default white section. */
  variant?: "light" | "dark"
  divider?: boolean
  /** Site-content editing pilot (Home page only): when set, heading and
   * subheading become click-to-edit fields instead of static text. */
  editable?: boolean
  onSaveHeading?: (value: string) => void
  onSaveSubheading?: (value: string) => void
  /** When provided together with `editable`, replaces `children` with
   * one click-to-edit paragraph per string. */
  body?: string[]
  onSaveBody?: (index: number, value: string) => void
}

export default function LandingTextSection({
  heading,
  subheading,
  children,
  variant = "light",
  divider = true,
  editable = false,
  onSaveHeading,
  onSaveSubheading,
  body,
  onSaveBody,
}: LandingTextSectionProps) {
  const isDark = variant === "dark"

  return (
    <section
      className={`px-4 py-16 md:px-8 ${isDark ? "bg-dantzBlue text-white" : "bg-white"}`}
    >
      <div className="mx-auto max-w-6xl">
        {divider && !isDark && (
          <div className="mx-auto mb-12 w-11/12 border-t border-dantzBlue" />
        )}

        <div className="mx-auto max-w-3xl text-center">
          <EditableText
            as="h2"
            value={heading}
            editMode={editable}
            onSave={(value) => onSaveHeading?.(value)}
            className={`text-[26px] font-light tracking-widest ${
              isDark ? "text-white" : "text-dantzBlue"
            }`}
          />

          {subheading && (
            <EditableText
              as="p"
              value={subheading}
              editMode={editable}
              onSave={(value) => onSaveSubheading?.(value)}
              className={`mt-3 text-base font-medium tracking-wide ${
                isDark ? "text-white" : "text-dantzBlue"
              }`}
            />
          )}

          <div
            className={`mt-6 space-y-4 text-sm font-light leading-relaxed ${
              isDark ? "text-slate-200" : "text-gray-600"
            }`}
          >
            {body
              ? body.map((paragraph, index) => (
                  <EditableText
                    key={index}
                    as="p"
                    value={paragraph}
                    editMode={editable}
                    multiline
                    onSave={(value) => onSaveBody?.(index, value)}
                  />
                ))
              : children}
          </div>
        </div>
      </div>
    </section>
  )
}
